import { IcuDiaryItem } from '@/api'
import { ReactComponent as InfoIcon } from '@/assets/info.svg'
import { ReactComponent as PenIcon } from '@/assets/pen.svg'
import { Loader, PrimaryButton } from '@/components'
import { useIcuDiary } from '@/services'
import { useModal } from '@/utils'
import dayjs from 'dayjs'
import 'dayjs/locale/de'
import { UIEventHandler, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { AddIcuDiary } from './AddIcuDiary'
import { DiaryHelpModal } from './DiaryHelpModal'

dayjs.locale('de')

const AuthorAvatar = ({ type, size = 36 }: { type?: string; size?: number }) => {
  const isProvider = type === 'hospital_worker'
  const icon = size / 2.2

  return (
    <div
      className={`rounded-full flex items-center justify-center shrink-0 ${
        isProvider ? 'bg-[#E8F4F2]' : 'bg-[#FFF0EC]'
      }`}
      style={{ width: size, height: size }}
    >
      {isProvider ? (
        <svg width={icon} height={icon} viewBox="0 0 16 16" fill="none">
          <path
            d="M8 1C6.34 1 5 2.34 5 4C5 5.66 6.34 7 8 7C9.66 7 11 5.66 11 4C11 2.34 9.66 1 8 1Z"
            fill="#6bbcb1"
          />
          <path
            d="M13 10H12V9C12 8.45 11.55 8 11 8C10.45 8 10 8.45 10 9V10H9C8.45 10 8 10.45 8 11C8 11.55 8.45 12 9 12H10V13C10 13.55 10.45 14 11 14C11.55 14 12 13.55 12 13V12H13C13.55 12 14 11.55 14 11C14 10.45 13.55 10 13 10Z"
            fill="#6bbcb1"
          />
          <path
            d="M7 9C4.79 9 1 10.34 1 13V14C1 14.55 1.45 15 2 15H7.5C7.22 14.39 7 13.72 7 13V9Z"
            fill="#6bbcb1"
          />
        </svg>
      ) : (
        <svg width={icon} height={icon} viewBox="0 0 16 16" fill="none">
          <path
            d="M8 14.4L7.1 13.58C3.72 10.52 1.6 8.58 1.6 6.2C1.6 4.26 3.14 2.72 5.08 2.72C6.18 2.72 7.24 3.24 8 4.06C8.76 3.24 9.82 2.72 10.92 2.72C12.86 2.72 14.4 4.26 14.4 6.2C14.4 8.58 12.28 10.52 8.9 13.58L8 14.4Z"
            fill="#E8836B"
          />
        </svg>
      )}
    </div>
  )
}

const authorName = (entry: IcuDiaryItem) =>
  entry.author_name || (entry.author_type === 'hospital_worker' ? 'Behandelnde:r' : 'Angehörige:r')

const DiaryListItem = ({
  entry,
  isActive,
  onClick,
}: {
  entry: IcuDiaryItem
  isActive: boolean
  onClick: () => void
}) => {
  const date = useMemo(() => dayjs(entry.creation_time), [entry.creation_time])

  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-left w-full px-3.5 py-3 rounded-2xl border transition-all duration-200 ${
        isActive
          ? 'bg-b0 border-primary/40 shadow-[0_4px_16px_rgba(85,160,190,0.14)] ring-1 ring-primary/15'
          : 'bg-b0 border-sec10/70 shadow-[0_2px_8px_rgba(0,0,0,0.05)] hover:border-sec30 hover:shadow-[0_4px_14px_rgba(0,0,0,0.08)]'
      }`}
    >
      <div className="flex items-center gap-2 mb-1">
        <AuthorAvatar type={entry.author_type} size={28} />
        <div className="min-w-0 flex-1">
          <div className="text-textS font-semibold text-b100 truncate">{authorName(entry)}</div>
          <div className="text-[11px] text-b50">{date.format('DD.MM.YYYY · HH:mm')}</div>
        </div>
      </div>
      <div className="text-textS text-b90 line-clamp-2 leading-snug">{entry.text}</div>
    </button>
  )
}

const DiaryEntryDetail = ({ entry }: { entry: IcuDiaryItem }) => {
  const date = useMemo(() => dayjs(entry.creation_time), [entry.creation_time])
  const isProvider = entry.author_type === 'hospital_worker'

  return (
    <div className="max-w-content w-full mx-auto p-8 flex flex-col gap-6">
      <div className="flex items-center gap-3">
        <AuthorAvatar type={entry.author_type} size={48} />
        <div>
          <div className="text-h4 text-b100">{authorName(entry)}</div>
          {entry.author_role && <div className="text-textS text-b50">{entry.author_role}</div>}
        </div>
        <div className="ml-auto text-right">
          <div className="text-textS font-medium text-sec100">
            {date.format('dddd, DD. MMMM YYYY')}
          </div>
          <div className="text-[12px] text-b50">{date.format('HH:mm')} Uhr</div>
        </div>
      </div>

      {entry.image && (
        <img
          src={entry.image}
          alt=""
          className="w-full max-h-[480px] object-contain rounded-2xl bg-sec0"
        />
      )}

      <p
        className="text-textBase text-b90 leading-[1.8] whitespace-pre-wrap"
        style={{
          borderLeft: `3px solid ${isProvider ? 'var(--accent)' : '#E8836B'}`,
          paddingLeft: '16px',
        }}
      >
        {entry.text}
      </p>
    </div>
  )
}

const Placeholder = ({ text }: { text: string }) => (
  <div className="flex flex-col items-center justify-center h-full text-center p-8 gap-3 text-b50">
    <PenIcon width={32} height={32} className="stroke-sec50 !fill-transparent" />
    <p className="text-textBase">{text}</p>
  </div>
)

type DiaryDesktopProps = {
  /** true wenn neue Einträge erlaubt sind (aktive:r Patient:in, kein Patient-User) */
  canAdd: boolean
}

export const DiaryDesktop = ({ canAdd }: DiaryDesktopProps) => {
  const { t } = useTranslation()
  const { patientId } = useParams<{ patientId: string }>()

  const { data = [], isFetched, hasNextPage, fetchNextPage, isFetchingNextPage } = useIcuDiary(
    patientId!,
  )

  const [mode, setMode] = useState<'view' | 'new'>('view')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const helpState = useModal(false)

  // Neueste zuerst in der Übersicht
  const sorted = useMemo(
    () =>
      [...data].sort(
        (a, b) => new Date(b.creation_time).getTime() - new Date(a.creation_time).getTime(),
      ),
    [data],
  )

  const selectedEntry = useMemo(() => {
    if (mode === 'new') return undefined
    return sorted.find((e) => e.id === selectedId) ?? sorted[0]
  }, [sorted, selectedId, mode])

  const scrollHandler = useCallback<UIEventHandler<HTMLDivElement>>(
    (e) => {
      const el = e.currentTarget
      if (
        el.scrollHeight - el.scrollTop - el.clientHeight < 300 &&
        hasNextPage &&
        !isFetchingNextPage
      ) {
        fetchNextPage()
      }
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage],
  )

  if (!isFetched) return <Loader />

  const openEntry = (id: string) => {
    setSelectedId(id)
    setMode('view')
  }

  return (
    <div className="flex w-full h-full">
      {/* Master: Eintragsübersicht */}
      <aside className="w-[380px] shrink-0 border-r border-sec30 flex flex-col h-full bg-sec0">
        <div className="p-4 flex flex-col gap-2 border-b border-sec30 shrink-0">
          <div className="flex items-center justify-between gap-2">
            <div className="text-h4 text-b100">{t('icuDiaryPage.title', 'Tagebuch')}</div>
            <InfoIcon
              fill="var(--sec100)"
              width={22}
              height={22}
              className="cursor-pointer shrink-0"
              onClick={helpState.open}
              aria-label={t('icuDiaryPage.help.title', 'Über das Intensivtagebuch')}
            />
          </div>
          {canAdd && (
            <PrimaryButton
              block
              className="svg:active:stroke-primary"
              onClick={() => setMode('new')}
            >
              <PenIcon width={20} height={20} className="stroke-b0 !fill-primary" />
              {t('icuDiaryPage.button')}
            </PrimaryButton>
          )}
          <p className="text-textXS text-b50">
            {t(
              'icuDiaryPage.confidentialityNote',
              'Ihre Einträge sind vertraulich – das Behandlungsteam kann Ihre persönlichen Einträge nicht sehen.',
            )}
          </p>
        </div>

        <div
          className="flex-1 overflow-y-auto px-3 py-3 flex flex-col gap-2.5"
          onScroll={scrollHandler}
        >
          {sorted.length === 0 ? (
            <p className="px-1 text-textS text-b50">
              {t('icuDiaryPage.desktop.empty', 'Noch keine Einträge.')}
            </p>
          ) : (
            sorted.map((entry) => (
              <DiaryListItem
                key={entry.id}
                entry={entry}
                isActive={mode === 'view' && selectedEntry?.id === entry.id}
                onClick={() => openEntry(entry.id)}
              />
            ))
          )}
          {hasNextPage && (
            <div className="h-[64px] flex items-center justify-center">
              <Loader size={28} />
            </div>
          )}
        </div>
      </aside>

      {/* Detail: Beitrag lesen oder neuen schreiben */}
      <section className="flex-1 h-full overflow-y-auto">
        {mode === 'new' ? (
          <AddIcuDiary wide onClose={() => setMode('view')} />
        ) : selectedEntry ? (
          <DiaryEntryDetail entry={selectedEntry} />
        ) : (
          <Placeholder
            text={
              canAdd
                ? t(
                    'icuDiaryPage.desktop.selectOrWrite',
                    'Wählen Sie links einen Eintrag oder schreiben Sie einen neuen.',
                  )
                : t('icuDiaryPage.desktop.select', 'Wählen Sie links einen Eintrag aus.')
            }
          />
        )}
      </section>

      {helpState.isOpen && <DiaryHelpModal onClose={helpState.close} />}
    </div>
  )
}
